import React, { useMemo, useState, useEffect, useRef } from 'react';
import { DataAssetWithRealAssetDeploymentResponse } from '../../apiClient';
import { prepAssetPreviewURL } from '../../apiUtils/downloadUrls';
import { FaImage } from "react-icons/fa6";
import Skeleton from '../loaders/Skeleton';

interface TimelineImageProps {
    dataAssets: DataAssetWithRealAssetDeploymentResponse[];
    selectedCamera?: string;
    className?: string;
    onHover?: (isHovered: boolean) => void;
}

const TimelineImage: React.FC<TimelineImageProps> = ({
    dataAssets = [],
    selectedCamera,
    className = '',
    onHover,
}) => {
    const [imageError, setImageError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const hasNoImages = !dataAssets || dataAssets.length === 0;

    const [isInViewport, setIsInViewport] = useState(false);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const elementRef = useRef<HTMLDivElement>(null);

    // Only compute image asset when in viewport
    const imageAsset = useMemo(() => {
        if (!isInViewport && !hasNoImages) return null;

        const imageAssets = dataAssets.filter(asset => {
            const extension = asset.path.split('.').pop()?.toLowerCase();
            return extension === 'jpg' || extension === 'jpeg' || extension === 'png';
        });

        if (imageAssets.length === 0) return null;

        if (selectedCamera) {
            const selectedCameraAsset = imageAssets.find(asset => {
                // todo: remove this "some" check once the duplicate daxrad records are removed
                return asset.real_asset_deployments?.some(deployment => {
                    return deployment.real_asset.name === selectedCamera;
                });
            });

            if (selectedCameraAsset) {
                return selectedCameraAsset;
            }
            return null;
        }

        return imageAssets[0];
    }, [dataAssets, hasNoImages, selectedCamera, isInViewport]);

    useEffect(() => {
        if (!elementRef.current) return;

        observerRef.current = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setIsInViewport(true);
                }
            },
            { threshold: 0.1 } // Trigger when at least 10% is visible
        );

        observerRef.current.observe(elementRef.current);
        return () => {
            observerRef.current?.disconnect();
        };
    }, []);

    const isImageUnavailable = !imageAsset || imageError;

    const handleImageError = () => {
        setImageError(true);
        setIsLoading(false);
    };

    const handleImageLoad = () => {
        setIsLoading(false);
    };

    const previewUrl = imageAsset?.public_id ? prepAssetPreviewURL(imageAsset as { public_id: string }) : '';

    const renderErrorState = () => {
        if (hasNoImages) {
            return (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100">
                    <FaImage className="text-gray-400 w-12 h-12" />
                    <span className="mt-4 text-gray-500 text-base px-2 text-center">
                        No Image Available For Event
                    </span>
                </div>
            );
        }
        if (selectedCamera && !imageAsset) {
            return (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100">
                    <FaImage className="text-gray-400 w-12 h-12" />
                    <span className="mt-4 text-gray-500 text-base px-2 text-center">
                        No Image Available for {selectedCamera} Camera
                    </span>
                </div>
            );
        }
    };

    return (
        <div
            ref={elementRef}
            className={`
                relative 
                w-full 
                h-[200px] 
                ${className}
                ${isImageUnavailable ? 'cursor-default' : 'cursor-pointer group'}
            `}
            onMouseEnter={() => !isImageUnavailable && onHover?.(true)}
            onMouseLeave={() => !isImageUnavailable && onHover?.(false)}
        >
            <div
                className={`
                    relative 
                    w-full 
                    h-full 
                    rounded-md 
                    overflow-hidden 
                    transition-shadow 
                    duration-300 
                    ease-in-out
                    ${!isImageUnavailable ? 'group-hover:shadow-md' : ''}
                `}
            >
                {isLoading && !imageError && imageAsset && (
                    <Skeleton className="absolute inset-0 z-10" />
                )}

                {isImageUnavailable ? (
                    renderErrorState()
                ) : (
                    <img
                        src={previewUrl}
                        alt={`Timeline image - ${imageAsset?.path.split('/').pop()}`}
                        className={`
                            w-full 
                            h-full 
                            object-cover 
                            transition-transform 
                            duration-300 
                            ease-in-out
                            ${isLoading ? 'opacity-0' : 'opacity-100'}
                            ${!isImageUnavailable ? 'group-hover:scale-110' : ''}
                        `}
                        loading="lazy"
                        onError={handleImageError}
                        onLoad={handleImageLoad}
                    />
                )}
            </div>
        </div>
    );
};

export default TimelineImage;